import { endOfDay, format, formatISO, startOfDay } from 'date-fns';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Suspense, useId, useMemo, useState } from 'react';
import { type DateRange } from 'react-day-picker';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage, useIntl } from 'react-intl';

import InfoIcon from '@/assets/icons/information-circle.svg';
import {
  DateRangePicker,
  DateRangePreset,
  LIFETIME,
  useDateRangePresetLabels,
} from '@/components/ui/date-range-picker';
import { TimeZoneChip, TimeZoneChipZone } from '@/components/ui/time-zone-chip';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useLocalDateRanges } from '@/hooks/use-local-date-ranges';
import { useLocalStorageState } from '@/hooks/use-local-storage-state';
import { cn } from '@/utils/classnames';
import { formatCalendarDateParam, lastDaysRange } from '@/utils/date-util';

import { Metrics } from './metrics';

const ANALYTICS_EXPANDED_KEY = 'source.dashboard.analyticsExpanded';
const ANALYTICS_PRESET_KEY = 'source.dashboard.analyticsPreset';

const DEFAULT_PRESET: DateRangePreset = '15';

function dateRangeFromPreset(
  preset: DateRangePreset,
  localDays: boolean,
): DateRange {
  if (preset === LIFETIME || preset === 'custom') {
    return lastDaysRange(15, localDays);
  }
  return lastDaysRange(Number(preset), localDays);
}

export function Analytics() {
  const intl = useIntl();
  const localDays = useLocalDateRanges();
  const presetLabels = useDateRangePresetLabels();
  const titleId = useId();

  const [isExpanded, setIsExpanded] = useLocalStorageState(
    ANALYTICS_EXPANDED_KEY,
    true,
  );

  // Persist only numeric/lifetime presets. Custom date picks are ephemeral —
  // on next visit the last numeric preset is restored and dates recomputed fresh.
  const [persistedPreset, setPersistedPreset] =
    useLocalStorageState<DateRangePreset>(ANALYTICS_PRESET_KEY, DEFAULT_PRESET);

  // Local preset drives the picker UI (can be 'custom'); persisted preset
  // drives what survives a refresh (never 'custom').
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>(
    () => persistedPreset,
  );

  const [date, setDate] = useState<DateRange>(() =>
    dateRangeFromPreset(persistedPreset, localDays),
  );

  // The picker re-reads its preset only when the prop changes, so a fallback to a
  // default it already holds needs a remount to drop its own 'custom'.
  const [pickerKey, setPickerKey] = useState(0);

  // A custom range missing either day falls back to the default preset; an empty
  // from_date or to_date matches nothing and shows all zeros.
  const handleDateChange = (range: DateRange) => {
    if (range.from && range.to) {
      setDate(range);
      return;
    }
    setSelectedPreset(DEFAULT_PRESET);
    setPersistedPreset(DEFAULT_PRESET);
    setDate(dateRangeFromPreset(DEFAULT_PRESET, localDays));
    setPickerKey(key => key + 1);
  };

  // Metrics API from_date / to_date: calendar dates the API reads at each opening,
  // or else local start-of-day / end-of-day ISO.
  const fromDate = useMemo(() => {
    if (!date.from) return '';
    return localDays
      ? formatCalendarDateParam(date.from)
      : formatISO(startOfDay(date.from));
  }, [date.from, localDays]);
  const toDate = useMemo(() => {
    if (!date.to) return '';
    return localDays
      ? formatCalendarDateParam(date.to)
      : formatISO(endOfDay(date.to));
  }, [date.to, localDays]);

  // Short label used in tooltip body copy (e.g. "last 15 days" or "Nov 22 – Jan 15").
  const dateRangeLabel = useMemo(() => {
    if (selectedPreset === 'custom') {
      if (!date.from || !date.to) return undefined;
      return `${format(date.from, 'LLL dd')} – ${format(date.to, 'LLL dd')}`;
    }
    return presetLabels[selectedPreset];
  }, [selectedPreset, date.from, date.to, presetLabels]);

  // Contextual label shown on the right side of the "Applicants & Hires" header.
  // Custom ranges show dates; presets use "in last N days" form.
  const sourcedHeaderLabel = useMemo(() => {
    if (selectedPreset === 'custom') {
      if (!date.from || !date.to) return undefined;
      return intl.formatMessage(
        {
          defaultMessage: 'Applicants sourced {start} – {end}',
          description:
            'Label showing applicants were counted between a custom {start} and {end} date',
        },
        {
          start: format(date.from, 'LLL dd'),
          end: format(date.to, 'LLL dd'),
        },
      );
    }
    return intl.formatMessage(
      {
        defaultMessage: 'Applicants sourced in {period}',
        description:
          'Label showing applicants were counted during a named time {period} such as the last 15 days',
      },
      { period: presetLabels[selectedPreset].toLowerCase() },
    );
  }, [selectedPreset, date.from, date.to, presetLabels, intl]);

  return (
    <ErrorBoundary fallback={null}>
      <div className="shadow-base flex w-full flex-col rounded-lg bg-white">
        {/* The whole row toggles on click; the chevron is the keyboard toggle. The row
            is not a button because it holds the date picker and the time zone chip. */}
        <div
          onClick={() => setIsExpanded(prev => !prev)}
          className={cn(
            'hover:bg-fx-neutral-50 flex cursor-pointer flex-row items-center gap-4 border-b border-neutral-200 px-4 py-1.5',
            isExpanded ? 'rounded-t-lg' : 'rounded-lg',
          )}
        >
          {/* Title */}
          <div className="flex items-center gap-1">
            <Typography variant="body2" id={titleId}>
              <FormattedMessage
                defaultMessage="Portfolio metrics"
                description="Heading for a summary of recruitment performance data"
              />
            </Typography>
            <Tooltip
              content={intl.formatMessage({
                defaultMessage:
                  'Spend, hires, and efficiency across all openings for the selected period.',
                description:
                  'Overview that spend, hires, and cost efficiency are measured across all job openings for the selected time period',
              })}
            >
              <img src={InfoIcon} alt="Info" className="h-4 w-4" />
            </Tooltip>
          </div>

          {/* Date filter */}
          <div onClick={e => e.stopPropagation()}>
            <DateRangePicker
              key={pickerKey}
              selectedDate={date}
              setSelectedDate={handleDateChange}
              selectedPreset={selectedPreset}
              onPresetChange={preset => {
                setSelectedPreset(preset);
                if (preset !== 'custom') setPersistedPreset(preset);
              }}
              showLifetimePreset={false}
              localDays={localDays}
            />
          </div>

          {localDays && (
            <div onClick={e => e.stopPropagation()}>
              <TimeZoneChip
                withInfoIcon
                tooltipTitle={
                  <FormattedMessage
                    defaultMessage="Why not one time zone for everything?"
                    description="Title of the tooltip on the Portfolio metrics time zone chip"
                  />
                }
                tooltipBody={
                  <>
                    <p>
                      <FormattedMessage
                        defaultMessage="Each opening runs in its own location’s time zone. The totals aggregate every opening over the same window, so a total never changes depending on who opens this page or where they are."
                        description="Tooltip on the Portfolio metrics time zone chip: why each opening counts in its own time zone"
                      />
                    </p>
                    <p>
                      <FormattedMessage
                        defaultMessage="For example, Oct 1–31 counts Oct 1–31 in America/New_York for a Tampa opening and Oct 1–31 in America/Los_Angeles for a Reno opening. The total adds both."
                        description="Tooltip on the Portfolio metrics time zone chip: an example of two openings in different time zones"
                      />
                    </p>
                  </>
                }
              >
                <FormattedMessage
                  defaultMessage="Each opening counts in <zone>its own location’s time zone</zone>"
                  description="Chip in the Portfolio metrics header: every opening's metrics count in its own location's time zone"
                  values={{
                    zone: chunks => (
                      <TimeZoneChipZone>{chunks}</TimeZoneChipZone>
                    ),
                  }}
                />
              </TimeZoneChip>
            </div>
          )}

          {/* Expand/collapse */}
          <button
            type="button"
            aria-expanded={isExpanded}
            aria-labelledby={titleId}
            className="ml-auto rounded"
          >
            {isExpanded ? (
              <ChevronUp aria-hidden className="text-fx-primary size-4" />
            ) : (
              <ChevronDown aria-hidden className="text-fx-primary size-4" />
            )}
          </button>
        </div>

        {isExpanded && (
          <div className="overflow-x-auto">
            <Suspense
              fallback={
                <Metrics.Skeleton
                  dateRangeLabel={dateRangeLabel}
                  sourcedHeaderLabel={sourcedHeaderLabel}
                />
              }
            >
              <Metrics
                fromDate={fromDate}
                toDate={toDate}
                dateRangeLabel={dateRangeLabel}
                sourcedHeaderLabel={sourcedHeaderLabel}
              />
            </Suspense>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}
